import { useEffect, useState } from 'react';

import { type SubmitHandler, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import logo from '@/assets/logo.svg';
import { BoxedIconMessage } from '@/components/BoxedIconMessage';
import { FormErrorMessage } from '@/components/FormErrorMessage';
import { ToastNotification } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StyledP } from '@/Shared/Typography/typography';

import {
	StyledBG,
	StyledFeatherBackgroundBottom,
	StyledFeatherBackgroundTop,
	StyledForm,
} from './ResetPasswordStyles';

interface FormValues {
	email: string;
}

const ResetPassword = () => {
	const {
		register,
		handleSubmit,
		formState: { isSubmitting, errors },
	} = useForm<FormValues>();
	const [messageState, setMessageState] = useState({
		message: '',
		messageColor: 'var(--green-300)',
		hasError: false,
	});

	const onSubmit: SubmitHandler<FormValues> = async (resetFormData) => {
		const { email } = resetFormData;

		try {
			await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-password-reset-email`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
				},
				body: JSON.stringify({
					email: email.trim(),
					appOrigin: window.location.origin,
				}),
			}).catch(() => {
				// Network errors — still show generic success below
			});

			// ALWAYS show success message (security: prevents email enumeration and information leakage)
			// This applies to:
			// - User exists: email sent successfully
			// - User doesn't exist: no email sent, but we don't reveal this
			// - Network errors: no email sent, but we don't reveal this
			// - Server errors: no email sent, but we don't reveal this
			// The "contact admin" note covers all edge cases
			setMessageState({
				message: `We've sent the password reset instructions to your email. Just give it a few minutes. Check your inbox, and if it's not there, take a peek in your spam folder. If you continue to have issues, please contact your administrator.`,
				messageColor: 'var(--green-300)',
				hasError: false,
			});
		} catch (error: unknown) {
			// Even for unexpected errors, show success message to prevent information leakage
			// This is the most secure approach per OWASP guidelines
			setMessageState({
				message: `We've sent the password reset instructions to your email—just give it a few minutes. Check your inbox, and if it's not there, take a peek in your spam folder. If you continue to have issues, please contact your administrator.`,
				messageColor: 'var(--green-300)',
				hasError: false,
			});
		}
	};

	// Auto-hide success message after 30 seconds
	useEffect(() => {
		if (messageState.message && !messageState.hasError) {
			const timer = setTimeout(() => {
				setMessageState((prev) => ({
					...prev,
					message: '',
					hasError: false,
				}));
			}, 30000);

			return () => clearTimeout(timer);
		}
		return undefined;
	}, [messageState.message, messageState.hasError]);

	return (
		<StyledBG>
			<StyledFeatherBackgroundTop />
			<StyledFeatherBackgroundBottom />
			<img src={`${logo}`} alt="Elevate logo" className="logo" />
			<StyledForm onSubmit={handleSubmit(onSubmit)} data-testid="form">
				<StyledP mb="var(--spacing-4)">
					Forgot your account's password or having trouble logging in? Enter your email address and
					we'll send you a recovery link.
				</StyledP>
				<StyledP mb="var(--spacing-8)">
					If you remember your password, please click <Link to="/login">here</Link> to login.
				</StyledP>
				<label htmlFor="email">Email Address</label>
				<Input
					type="text"
					id="email"
					data-testid="email"
					{...register('email', {
						required: 'Email is required',
						pattern: {
							value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\s*$/i,
							message: 'Invalid email address',
						},
					})}
				/>
				<FormErrorMessage
					className="error-message"
					isShowing={!!errors?.email}
					message={errors.email?.message}
				/>
				<Button type="submit" disabled={isSubmitting} isLoading={isSubmitting} className="w-max">
					Send Email
				</Button>
				{messageState.message !== '' && (
					<BoxedIconMessage
						message={messageState.message}
						messageColor={messageState.messageColor}
						hasError={messageState.hasError}
					/>
				)}
			</StyledForm>
			<ToastNotification />
		</StyledBG>
	);
};

export default ResetPassword;
